import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Clapperboard,
  Clock3,
  KeyRound,
  Megaphone,
  MonitorSmartphone,
  Music,
  PlayCircle,
  Radio,
  Settings2,
  Tv,
  UserCheck,
  Users,
  Wifi,
  WifiOff,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, mediaUrl, type Device, type LineupEntry } from '../api/client';
import { NeedsSite } from '../app/Layout';
import { useDeviceLiveUpdates } from '../app/live';
import { useSession, useSite } from '../app/session';
import type { Stay } from './GuestsPage';
import type { Message } from './MarketingPage';
import './DashboardPage.css';

/** Same layout as the PulsePanels welcome dashboard, with the hotel TV figures. */
export function DashboardPage() {
  const site = useSite();
  return <NeedsSite>{site && <SiteDashboard siteId={site.id} siteName={site.name} rooms={site._count?.rooms ?? 0} />}</NeedsSite>;
}

const fmt = (n: number) => new Intl.NumberFormat().format(n);
const pct = (v: number) => `${Math.round(Math.max(0, Math.min(100, v)))}%`;
const greeting = (h: number) => (h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening');
const clock = (ms: number) => new Date(ms).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
const time = (iso: string | null | undefined) => (iso ? new Date(iso).getTime() || 0 : 0);

function ago(ms: number, now: number) {
  if (!ms) return '—';
  const m = Math.floor(Math.max(0, now - ms) / 60_000);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr ago`;
  return `${Math.floor(h / 24)} days ago`;
}

const platformName = (p: string) => (p === 'LG_PROCENTRIC' || /lg/i.test(p) ? 'LG' : /android/i.test(p) ? 'Android' : p);

function StatusDonut({ segments }: { segments: { key: string; value: number; color: string }[] }) {
  const r = 54;
  const c = 2 * Math.PI * r;
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;
  let offset = 0;
  return (
    <svg viewBox="0 0 138 138" aria-hidden="true">
      <circle cx="69" cy="69" r={r} fill="none" stroke="#f1eefb" strokeWidth="16" />
      {segments.map((seg) => {
        if (seg.value <= 0) return null;
        const dash = (seg.value / total) * c;
        const el = (
          <circle key={seg.key} cx="69" cy="69" r={r} fill="none" stroke={seg.color} strokeWidth="16"
            strokeDasharray={`${dash} ${c - dash}`} strokeDashoffset={-offset} transform="rotate(-90 69 69)" />
        );
        offset += dash;
        return el;
      })}
    </svg>
  );
}

function SiteDashboard({ siteId, siteName, rooms }: { siteId: string; siteName: string; rooms: number }) {
  useDeviceLiveUpdates();
  const navigate = useNavigate();
  const { me } = useSession();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(t);
  }, []);

  const devicesQ = useQuery({ queryKey: ['devices', siteId], queryFn: () => api.get<{ items: Device[] }>(`/sites/${siteId}/devices`) });
  const lineupQ = useQuery({ queryKey: ['lineup', siteId], queryFn: () => api.get<{ items: LineupEntry[] }>(`/sites/${siteId}/lineup`) });
  const staysQ = useQuery({ queryKey: ['stays', siteId, 'IN_HOUSE'], queryFn: () => api.get<{ items: Stay[] }>(`/sites/${siteId}/stays?status=IN_HOUSE`) });
  const messagesQ = useQuery({ queryKey: ['messages', siteId, 'campaigns'], queryFn: () => api.get<{ items: Message[] }>(`/sites/${siteId}/messages?view=campaigns`) });
  const emergencyQ = useQuery({ queryKey: ['messages', siteId, 'emergency'], queryFn: () => api.get<{ items: Message[] }>(`/sites/${siteId}/messages?view=emergency`) });
  const vodQ = useQuery({ queryKey: ['vod', 'items'], queryFn: () => api.get<{ items: unknown[] }>('/vod/titles') });
  const aodQ = useQuery({ queryKey: ['aod', 'items'], queryFn: () => api.get<{ items: unknown[] }>('/aod/tracks') });

  const devices = devicesQ.data?.items ?? [];
  const lineup = lineupQ.data?.items ?? [];
  const stays = staysQ.data?.items ?? [];
  const campaigns = messagesQ.data?.items ?? [];
  const emergencies = (emergencyQ.data?.items ?? []).filter((m) => m.status === 'ACTIVE');
  const loading = devicesQ.isLoading || lineupQ.isLoading;

  const tv = useMemo(() => {
    const s = { total: devices.length, activated: 0, online: 0, offline: 0, disabled: 0, pending: 0, watching: 0, lg: 0, android: 0, upToDate: 0 };
    for (const d of devices) {
      if (platformName(d.platform) === 'LG') s.lg += 1;
      else s.android += 1;
      if (d.status !== 'ACTIVE') s.disabled += 1;
      else if (!d.activated) s.pending += 1;
      else if (d.online) s.online += 1;
      else s.offline += 1;
      if (d.activated) s.activated += 1;
      if (d.activated && d.appliedConfigEtag) s.upToDate += 1;
      if (d.online && d.currentChannel) s.watching += 1;
    }
    return s;
  }, [devices]);

  const streams = useMemo(() => {
    const s = { enabled: 0, ok: 0, failed: 0, unchecked: 0, failing: [] as LineupEntry[] };
    for (const e of lineup) {
      if (!e.enabled) continue;
      s.enabled += 1;
      const checks = e.sources.filter((x) => x.enabled).map((x) => x.checkStatus);
      if (checks.some((c) => c === 'OK')) s.ok += 1;
      else if (checks.some((c) => c && c !== 'OK')) {
        s.failed += 1;
        s.failing.push(e);
      } else s.unchecked += 1;
    }
    return s;
  }, [lineup]);

  const activeCampaigns = campaigns.filter((m) => m.status === 'ACTIVE').length;
  const onlinePct = tv.total ? (tv.online / tv.total) * 100 : 0;
  const offlinePct = tv.total ? (tv.offline / tv.total) * 100 : 0;
  const streamPct = streams.ok + streams.failed ? (streams.ok / (streams.ok + streams.failed)) * 100 : 100;
  const score = tv.total === 0 ? 100 : Math.round(Math.max(0, Math.min(100, onlinePct * 0.7 + streamPct * 0.3)));
  const scoreLabel = score >= 85 ? 'Excellent' : score >= 70 ? 'Good' : score >= 50 ? 'Fair' : 'Needs attention';
  const critical = emergencies.length + (tv.activated > 0 && tv.online === 0 ? 1 : 0);
  const warnings = (tv.offline > 0 ? 1 : 0) + (streams.failed > 0 ? 1 : 0) + (tv.pending > 0 ? 1 : 0);
  const occupancy = rooms ? (stays.length / rooms) * 100 : 0;

  const channelByNumber = useMemo(() => new Map(lineup.map((e) => [e.number, e])), [lineup]);
  // TVs on a channel first, then online ones, by room; the list keeps its order between refreshes.
  const nowWatching = useMemo(
    () =>
      [...devices]
        .filter((d) => d.activated && d.status === 'ACTIVE')
        .sort((a, b) => Number(!!b.currentChannel && b.online) - Number(!!a.currentChannel && a.online) || Number(b.online) - Number(a.online) || (a.room?.number ?? a.name).localeCompare(b.room?.number ?? b.name, undefined, { numeric: true }))
        .slice(0, 3),
    [devices],
  );

  const activity = useMemo(() => {
    type Item = { id: string; time: number; tone: string; icon: React.ReactNode; title: string; subtitle: string };
    const items: Item[] = [];
    for (const d of devices) {
      const where = d.room ? `Room ${d.room.number}` : d.name;
      if (d.bootedAt) items.push({ id: `boot-${d.id}`, time: time(d.bootedAt), tone: 'green', icon: <Wifi />, title: `${where} TV started`, subtitle: `${platformName(d.platform)}${d.model ? ` · ${d.model}` : ''}` });
      if (d.activated && !d.online && d.status === 'ACTIVE' && d.lastSeenAt) items.push({ id: `off-${d.id}`, time: time(d.lastSeenAt), tone: 'red', icon: <WifiOff />, title: `${where} TV went offline`, subtitle: d.name });
    }
    for (const s of stays) items.push({ id: `stay-${s.id}`, time: time(s.checkInAt), tone: 'blue', icon: <UserCheck />, title: `${s.displayName || [s.title, s.lastName].filter(Boolean).join(' ') || 'Guest'} checked in`, subtitle: `Room ${s.room.number}` });
    for (const m of campaigns) items.push({ id: `msg-${m.id}`, time: time(m.updatedAt), tone: 'purple', icon: <Megaphone />, title: `Message “${m.name || m.title || 'Untitled'}” ${m.status === 'ACTIVE' ? 'is live' : m.status.toLowerCase()}`, subtitle: 'Marketing' });
    return items.filter((i) => i.time > 0).sort((a, b) => b.time - a.time).slice(0, 5);
  }, [devices, stays, campaigns]);

  const lifecycle = useMemo(() => {
    const total = tv.total;
    const stages = [
      { key: 'registered', label: 'Registered', value: total, color: '#6d28ff', icon: <MonitorSmartphone /> },
      { key: 'activated', label: 'Activated', value: tv.activated, color: '#1677ff', icon: <KeyRound /> },
      { key: 'configured', label: 'Configured', value: tv.upToDate, color: '#7c3aed', icon: <Settings2 /> },
      { key: 'online', label: 'Online', value: tv.online, color: '#ff9900', icon: <CheckCircle2 /> },
      { key: 'watching', label: 'Watching', value: tv.watching, color: '#13b981', icon: <PlayCircle /> },
    ];
    return stages.map((s) => ({ ...s, pct: total ? Math.round((s.value / total) * 100) : 0 }));
  }, [tv]);

  const quickActions = [
    { label: 'Activate a TV', icon: <MonitorSmartphone />, to: '/devices' },
    { label: 'Channel lineup', icon: <Radio />, to: '/content/live-tv' },
    { label: 'Check in guest', icon: <UserCheck />, to: '/guests' },
    { label: 'Send a message', icon: <Megaphone />, to: '/marketing' },
    { label: 'Add movies', icon: <Clapperboard />, to: '/content/vod' },
  ];

  const donut = [
    { key: 'online', label: 'Online', value: tv.online, color: '#13b981' },
    { key: 'offline', label: 'Offline', value: tv.offline, color: '#ff4d5e' },
    { key: 'disabled', label: 'Disabled', value: tv.disabled, color: '#9ca3af' },
    { key: 'pending', label: 'Awaiting activation', value: tv.pending, color: '#ff9900' },
  ];
  const firstName = (me.user.name || '').split(' ')[0] || 'there';
  const dash = (v: string) => (loading ? '…' : v);

  return (
    <div className="welcome-dashboard">
      <header className="dashboard-header">
        <div>
          <div className="dashboard-header__greeting">{greeting(new Date(now).getHours())}, {firstName} 👋</div>
          <div className="dashboard-header__title-row">
            <h1 className="dashboard-header__title">{siteName}</h1>
          </div>
        </div>
        <div className="dashboard-header__right">
          <div className="dashboard-header__status">
            <span>Last updated: {clock(now)}</span>
            <span className="live-badge">Live</span>
          </div>
        </div>
      </header>

      <section className="dashboard-top-grid">
        <div className="workspace-health-card" style={{ '--pp-score': score } as React.CSSProperties}>
          <div className="health-score-ring"><div className="health-score-ring__value">{score}</div></div>
          <div className="workspace-health-card__content">
            <p className="workspace-health-card__eyebrow">System health</p>
            <div className="workspace-health-card__heading-row">
              <h2 className="workspace-health-card__heading">{scoreLabel}</h2>
            </div>
            <p className="workspace-health-card__description">
              {critical === 0 && warnings === 0 ? 'All TVs and channels are running smoothly.' : 'Some TVs or channels need your attention.'}
            </p>
            <button type="button" className="health-details-button" onClick={() => navigate('/devices')}>
              View TVs <ChevronRight />
            </button>
          </div>
          <div className="workspace-health-card__stats">
            <div className="health-stat">
              <span className="health-stat__icon health-stat__icon--red"><AlertCircle /></span>
              <span className="health-stat__value">{critical}</span>
              <span className="health-stat__label">Critical alerts</span>
            </div>
            <div className="health-stat">
              <span className="health-stat__icon health-stat__icon--orange"><AlertTriangle /></span>
              <span className="health-stat__value">{warnings}</span>
              <span className="health-stat__label">Warnings</span>
            </div>
            <div className="health-stat">
              <span className="health-stat__icon health-stat__icon--green"><Clock3 /></span>
              <span className="health-stat__value">{pct(streamPct)}</span>
              <span className="health-stat__label">Channels streaming</span>
            </div>
          </div>
        </div>

        <div className="dashboard-card quick-actions-card">
          <div className="dashboard-card__header"><h2 className="dashboard-card__title">Quick actions</h2></div>
          <div className="quick-actions-grid">
            {quickActions.map((a) => (
              <button type="button" key={a.label} className="quick-action" onClick={() => navigate(a.to)}>
                <span className="quick-action__icon quick-action__icon--purple">{a.icon}</span>
                <span className="quick-action__label">{a.label}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="dashboard-kpi-grid" aria-label="Key figures">
        <div className="dashboard-card kpi-card">
          <p className="kpi-card__label">Total TVs</p>
          <p className="kpi-card__value">{dash(fmt(tv.total))}</p>
          <div className="kpi-card__meta">{fmt(tv.android)} Android · {fmt(tv.lg)} LG</div>
          <span className="kpi-card__icon kpi-icon--purple"><Tv /></span>
        </div>
        <div className="dashboard-card kpi-card">
          <p className="kpi-card__label">Online</p>
          <p className="kpi-card__value">{dash(fmt(tv.online))}</p>
          <div className="kpi-card__meta kpi-card__meta--positive">{pct(onlinePct)} of total</div>
          <span className="kpi-card__icon kpi-icon--green"><Wifi /></span>
        </div>
        <div className="dashboard-card kpi-card">
          <p className="kpi-card__label">Offline</p>
          <p className="kpi-card__value">{dash(fmt(tv.offline))}</p>
          <div className={`kpi-card__meta ${tv.offline ? 'kpi-card__meta--negative' : ''}`}>{pct(offlinePct)} of total</div>
          <span className="kpi-card__icon kpi-icon--red"><WifiOff /></span>
        </div>
        <div className="dashboard-card kpi-card">
          <p className="kpi-card__label">Live channels</p>
          <p className="kpi-card__value">{dash(fmt(streams.enabled))}</p>
          <div className={`kpi-card__meta ${streams.failed ? 'kpi-card__meta--negative' : ''}`}>{fmt(streams.ok)} streaming, {fmt(streams.failed)} failing</div>
          <span className="kpi-card__icon kpi-icon--violet"><Radio /></span>
        </div>
        <div className="dashboard-card kpi-card">
          <p className="kpi-card__label">Guests in house</p>
          <p className="kpi-card__value">{staysQ.isLoading ? '…' : fmt(stays.length)}</p>
          <div className="kpi-card__meta">{pct(occupancy)} of {fmt(rooms)} rooms</div>
          <span className="kpi-card__icon kpi-icon--blue"><Users /></span>
        </div>
        <div className="dashboard-card kpi-card">
          <p className="kpi-card__label">Entertainment</p>
          <p className="kpi-card__value">{vodQ.isLoading ? '…' : fmt(vodQ.data?.items.length ?? 0)}</p>
          <div className="kpi-card__meta">movies · {fmt(aodQ.data?.items.length ?? 0)} songs · {fmt(activeCampaigns)} live messages</div>
          <span className="kpi-card__icon kpi-icon--green"><Music /></span>
        </div>
      </section>

      <section className="dashboard-analytics-grid">
        <div className="dashboard-card analytics-card">
          <div className="dashboard-card__header"><h2 className="dashboard-card__title">TV status</h2></div>
          <div className="distribution-content">
            <div className="distribution-donut">
              <StatusDonut segments={donut} />
              <div className="distribution-donut__center">
                <div className="distribution-donut__value">{fmt(tv.total)}</div>
                <div className="distribution-donut__label">TVs</div>
              </div>
            </div>
            <div className="distribution-legend">
              {donut.map((d) => (
                <div className="distribution-legend__row" key={d.key}>
                  <span className="distribution-legend__dot" style={{ background: d.color }} />
                  <span>{d.label}</span>
                  <span className="distribution-legend__value">{fmt(d.value)} ({pct(tv.total ? (d.value / tv.total) * 100 : 0)})</span>
                </div>
              ))}
            </div>
          </div>
          <div className="distribution-card__footer">
            <button type="button" className="dashboard-card__link" onClick={() => navigate('/devices')}>View all TVs <ChevronRight /></button>
          </div>
        </div>

        <div className="dashboard-card analytics-card">
          <div className="dashboard-card__header">
            <h2 className="dashboard-card__title">Now watching</h2>
            <button type="button" className="dashboard-card__link" onClick={() => navigate('/devices')}>View all <ChevronRight /></button>
          </div>
          <div className="live-screen-list">
            {nowWatching.length ? (
              nowWatching.map((d) => {
                const watching = d.online && !!d.currentChannel;
                const logo = d.currentChannel ? channelByNumber.get(d.currentChannel.number)?.channel.logo : null;
                return (
                  <button type="button" key={d.id} className="live-screen-row" onClick={() => navigate('/devices')}>
                    <span className={`live-screen-row__thumbnail ${watching ? 'live-screen-row__thumbnail--live' : ''}`}>
                      {logo ? (
                        <img src={mediaUrl(logo.storageKey)} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', padding: 6, background: '#fff' }} />
                      ) : (
                        <span className="live-tile-fallback"><Tv /></span>
                      )}
                    </span>
                    <span style={{ minWidth: 0 }}>
                      <div className="live-screen-row__name">{d.room ? `Room ${d.room.number}` : d.name}</div>
                      <div className="live-screen-row__location">{d.currentChannel ? `${d.currentChannel.number} · ${d.currentChannel.name}` : 'Not on a channel'}</div>
                    </span>
                    <span className={`screen-status-badge ${d.online ? 'screen-status-badge--live' : 'screen-status-badge--offline'}`}>{d.online ? (watching ? 'LIVE' : 'ON') : 'OFFLINE'}</span>
                    <span className="live-screen-row__time">{d.online ? platformName(d.platform) : ago(time(d.lastSeenAt), now)}</span>
                  </button>
                );
              })
            ) : (
              <div className="live-screen-row__location" style={{ textAlign: 'center', padding: '20px 0' }}>No activated TVs yet.</div>
            )}
          </div>
          <div className="live-activity-summary">
            <span><Wifi /> {fmt(tv.online)} online</span>
            <span><WifiOff /> {fmt(tv.offline)} offline</span>
            <span><PlayCircle /> {fmt(tv.watching)} watching TV</span>
          </div>
        </div>

        <div className="dashboard-card analytics-card">
          <div className="dashboard-card__header">
            <h2 className="dashboard-card__title">Channel streams</h2>
            <button type="button" className="dashboard-card__link" onClick={() => navigate('/content/live-tv')}>View lineup <ChevronRight /></button>
          </div>
          <div className="playlist-readiness-list">
            {[
              { key: 'ok', name: 'Streaming', value: streams.ok, cls: 'is-ok', badge: 'pr-badge--published', note: 'Checked and playing' },
              { key: 'failed', name: 'Failing', value: streams.failed, cls: 'is-warn', badge: 'pr-badge--draft', note: streams.failing.slice(0, 3).map((e) => `${e.number} ${e.channel.name}`).join(', ') || 'None' },
              { key: 'unchecked', name: 'Not checked yet', value: streams.unchecked, cls: 'is-warn', badge: 'pr-badge--draft', note: 'Run the stream check in Live TV' },
            ].map((r) => {
              const p = streams.enabled ? Math.round((r.value / streams.enabled) * 100) : 0;
              return (
                <div className="pr-row" key={r.key}>
                  <div className="pr-top">
                    <span className="pr-name">{r.name}</span>
                    <span className={`pr-badge ${r.badge}`}>{fmt(r.value)} channels</span>
                    <span className="pr-pct">{p}%</span>
                  </div>
                  <div className="pr-sub">
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.note}</span>
                  </div>
                  <div className="pr-bar"><div className={`pr-bar-fill ${r.cls}`} style={{ width: `${p}%` }} /></div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="dashboard-bottom-grid">
        <div className="dashboard-card bottom-card">
          <div className="dashboard-card__header">
            <h2 className="dashboard-card__title">Recent activity</h2>
            <button type="button" className="dashboard-card__link" onClick={() => navigate('/devices')}>View all <ChevronRight /></button>
          </div>
          <div className="recent-activity-list">
            {activity.length ? (
              activity.map((item) => (
                <div className="recent-activity-row" key={item.id}>
                  <span className={`activity-icon activity-icon--${item.tone}`}>{item.icon}</span>
                  <span style={{ minWidth: 0 }}>
                    <div className="recent-activity-row__title">{item.title}</div>
                    <div className="recent-activity-row__subtitle">{item.subtitle}</div>
                  </span>
                  <span className="recent-activity-row__time">{ago(item.time, now)}</span>
                </div>
              ))
            ) : (
              <div className="recent-activity-row__subtitle" style={{ textAlign: 'center', padding: '20px 0' }}>No recent activity.</div>
            )}
          </div>
        </div>

        <div className="dashboard-card bottom-card">
          <div className="dashboard-card__header">
            <h2 className="dashboard-card__title">TV life cycle</h2>
            <button type="button" className="dashboard-card__link" onClick={() => navigate('/devices')}>View all TVs <ChevronRight /></button>
          </div>
          <div className="lifecycle-steps">
            {lifecycle.map((stage, idx) => (
              <React.Fragment key={stage.key}>
                <div className="lifecycle-step">
                  <span className="lifecycle-step__icon" style={{ color: stage.color, background: `${stage.color}1f` }}>{stage.icon}</span>
                  <span className="lifecycle-step__label">{stage.label}</span>
                  <span className="lifecycle-step__count">{dash(fmt(stage.value))}</span>
                  <span className="lifecycle-step__pct">{stage.pct}%</span>
                </div>
                {idx < lifecycle.length - 1 ? <span className="lifecycle-arrow"><ChevronRight /></span> : null}
              </React.Fragment>
            ))}
          </div>
          <div className="lifecycle-bar">
            {lifecycle.map((stage) => <span key={stage.key} style={{ background: stage.color }} />)}
          </div>
          <div className="lifecycle-summary">{fmt(tv.watching)} of {fmt(tv.total)} TVs are watching a channel</div>
        </div>
      </section>

      <footer className="dashboard-footer">© {new Date(now).getFullYear()} PulseIPTV. All rights reserved.</footer>
    </div>
  );
}
